<template>
  <div>
    <div class="searchArea">
      <a-range-picker v-model:value="dateRange" :placeholder="['开始日期', '结束日期']" />
      <a-button type="primary" shape="circle" @click="handleSearch">
        <template #icon>
          <SearchOutlined />
        </template>
      </a-button>
      <a-button style="margin-left: 10px" @click="handleReset">重置</a-button>
    </div>

    <a-table class="ant-table-striped" :columns="columns" :data-source="data"
      :rowClassName="(record, index) => (index % 2 === 1 ? 'table-striped' : null)" bordered rowKey="date"
      :scroll="{ x: scrollX }" :loading="loading">
      <template #summary>
        <a-table-summary>
          <a-table-summary-row>
            <a-table-summary-cell v-for="col in columns" :key="col.key" :index="columns.indexOf(col)">
              <template v-if="col.dataIndex === 'date'">
                <strong>{{ summaryData.date }}</strong>
              </template>
              <template v-else>
                <strong>{{ summaryData[col.dataIndex] ?? '' }}</strong>
              </template>
            </a-table-summary-cell>
          </a-table-summary-row>
        </a-table-summary>
      </template>
    </a-table>
  </div>
</template>

<script>
import { SearchOutlined } from "@ant-design/icons-vue";
import { DailySummary } from "/src/api/appointment.js";
import moment from "moment";

export default {
  name: "预约汇总",
  components: { SearchOutlined },
  data() {
    return {
      dateRange: [],
      loading: false,
      columns: [],
      data: [],
      doctorNames: [],
      summaryData: {},
    };
  },
  computed: {
    scrollX() {
      return 450 + this.doctorNames.length * 120;
    },
  },
  mounted() {
    const from = moment().subtract(6, "days").format("YYYY-MM-DD");
    const to = moment().format("YYYY-MM-DD");
    this.dateRange = [moment(from, "YYYY-MM-DD"), moment(to, "YYYY-MM-DD")];
    this.handleSearch();
  },
  methods: {
    getRange() {
      const from = this.dateRange && this.dateRange[0] ? this.dateRange[0].format("YYYY-MM-DD") : undefined;
      const to = this.dateRange && this.dateRange[1] ? this.dateRange[1].format("YYYY-MM-DD") : undefined;
      return { dateFrom: from, dateTo: to };
    },
    buildColumns(doctorNames) {
      const fixedColumns = [
        {
          title: "日期",
          dataIndex: "date",
          key: "date",
          width: 120,
          fixed: "left",
          align: "center",
        },
        {
          title: "预约总数",
          dataIndex: "total",
          key: "total",
          width: 100,
          align: "center",
        },
        {
          title: "医助预约",
          dataIndex: "assistantCount",
          key: "assistantCount",
          width: 100,
          align: "center",
        },
        {
          title: "客户预约",
          dataIndex: "customerCount",
          key: "customerCount",
          width: 100,
          align: "center",
        },
      ];

      const doctorColumns = doctorNames.map((name) => ({
        title: name,
        dataIndex: name,
        key: name,
        width: 120,
        align: "center",
      }));

      return [...fixedColumns, ...doctorColumns];
    },
    calcSummary() {
      if (!this.data.length) {
        this.summaryData = {};
        return;
      }
      const total = this.data.length;
      const summary = {
        date: `共 ${total} 天`,
        total: 0,
        assistantCount: 0,
        customerCount: 0,
      };
      this.doctorNames.forEach((name) => {
        summary[name] = 0;
      });
      this.data.forEach((row) => {
        summary.total += row.total || 0;
        summary.assistantCount += row.assistantCount || 0;
        summary.customerCount += row.customerCount || 0;
        this.doctorNames.forEach((name) => {
          summary[name] += row[name] || 0;
        });
      });
      this.summaryData = summary;
    },
    async handleSearch() {
      this.loading = true;
      try {
        const range = this.getRange();
        if (!range.dateFrom || !range.dateTo) {
          this.$message.warning("请选择日期范围");
          return;
        }
        const res = await DailySummary(range);
        if (res.errCode === 0) {
          const rawData = res.data || [];

          // 收集所有医生名称（去重，保留顺序）
          const nameSet = new Set();
          rawData.forEach((item) => {
            (item.doctorCounts || []).forEach((dc) => {
              nameSet.add(dc.doctorName);
            });
          });
          this.doctorNames = Array.from(nameSet);

          // 构建表格列
          this.columns = this.buildColumns(this.doctorNames);

          // 构建表格数据：每个日期一条记录，医生名作为动态列
          this.data = rawData.map((item) => {
            const row = {
              date: item.date,
              total: item.total || 0,
              assistantCount: item.assistantCount || 0,
              customerCount: item.customerCount || 0,
            };
            (item.doctorCounts || []).forEach((dc) => {
              row[dc.doctorName] = dc.count || 0;
            });
            // 没有排班的医生补 0
            this.doctorNames.forEach((name) => {
              if (row[name] === undefined) {
                row[name] = 0;
              }
            });
            return row;
          });

          // 计算合计
          this.calcSummary();

          // 更新列标题，将合计值追加到 header 后面（原地修改，避免触发表格重渲染导致日期选择器状态丢失）
          this.columns.forEach(col => {
            if (col.dataIndex === 'date') {
              col.title = '日期';
              return;
            }
            const val = this.summaryData[col.dataIndex];
            col.title = `${col.title} (${val ?? 0})`;
          });
        } else {
          this.$message.error(res.errMsg || "查询失败");
        }
      } catch (error) {
        console.error("DailySummary search error:", error);
        this.$message.error("请求异常，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    handleReset() {
      const from = moment().subtract(6, "days").format("YYYY-MM-DD");
      const to = moment().format("YYYY-MM-DD");
      this.dateRange = [moment(from, "YYYY-MM-DD"), moment(to, "YYYY-MM-DD")];
      this.handleSearch();
    },
  },
};
</script>

<style scoped>
.searchArea {
  background-color: #fff;
  padding: 18px 18px 0;
  margin-bottom: 16px;
}
</style>